import {
  IEtlJobSummary,
  IEtlJobSummaryWorker,
  IEtlSummaryBlock,
} from "@ryde/shared";

import { ETL_SUMMARY_DOMAIN_VARIANTS } from "./etl-summary.constants";
import { EtlSummaryTileVariant, IEtlSummaryTile } from "./etl-summary.models";

// Format: [F.name worker.code - unit.name unit.ID], e.g. [Dana 012445 - Sales 10185].
// Uses `code` (the passenger/employee code) to match the PassengerEtlSummary alert email formatter.
export const formatWorkerLabel = (worker: IEtlJobSummaryWorker): string => {
  const namePart = [worker.firstName, worker.code].filter(Boolean).join(" ");
  const unitPart = [worker.unitName, worker.unitId]
    .filter(value => value !== undefined && value !== null && value !== "")
    .join(" ");

  return unitPart ? `${namePart} - ${unitPart}` : `${namePart}`;
};

const formatBlockValue = (block: IEtlSummaryBlock): string =>
  block.format === "duration" ? `${block.value}s` : `${block.value}`;

const hasWorkerFields = (summary: IEtlJobSummary): boolean =>
  summary.existingWorkers !== undefined ||
  summary.newWorkersCount !== undefined ||
  summary.removedWorkersCount !== undefined;

/** Title i18n key: per-ETL when the loader set one, else the passenger report title (legacy rows), else generic. */
export const resolveEtlSummaryTitleKey = (summary: IEtlJobSummary): string => {
  if (summary.titleKey) {
    return summary.titleKey;
  }

  return hasWorkerFields(summary)
    ? "etlJobLog.summaryReport"
    : "etlJobLog.summaryTitle.default";
};

/**
 * The (up to 4) tiles to render, always ending with Load Duration. Priority:
 *   1. generic per-ETL blocks (bookings created, stations upserted, ...);
 *   2. legacy passenger worker fields (rows written before the blocks model — card looks unchanged);
 *   3. generic fallback from the always-present rowsRead / rowsWritten.
 */
export const resolveEtlSummaryTiles = ({
  summary,
  rowsRead,
  rowsWritten,
}: {
  summary: IEtlJobSummary;
  rowsRead?: number;
  rowsWritten?: number;
}): IEtlSummaryTile[] => {
  const durationTile: IEtlSummaryTile = {
    labelKey: "etlJobLog.summaryLabel.loadDuration",
    value: `${summary.loadDurationSeconds ?? 0}s`,
    variant: EtlSummaryTileVariant.Duration,
  };

  if (summary.blocks?.length) {
    const domainTiles: IEtlSummaryTile[] = summary.blocks
      .slice(0, ETL_SUMMARY_DOMAIN_VARIANTS.length)
      .map((block, index) => ({
        labelKey: block.labelKey,
        value: formatBlockValue(block),
        variant:
          ETL_SUMMARY_DOMAIN_VARIANTS[index] ?? EtlSummaryTileVariant.Existing,
      }));

    return [...domainTiles, durationTile];
  }

  if (hasWorkerFields(summary)) {
    return [
      {
        labelKey: "etlJobLog.summaryLabel.existingWorkers",
        value: `${summary.existingWorkers ?? 0}`,
        variant: EtlSummaryTileVariant.Existing,
      },
      {
        labelKey: "etlJobLog.summaryLabel.newWorkers",
        value: `${summary.newWorkersCount ?? 0}`,
        variant: EtlSummaryTileVariant.New,
      },
      {
        labelKey: "etlJobLog.summaryLabel.removedWorkers",
        value: `${summary.removedWorkersCount ?? 0}`,
        variant: EtlSummaryTileVariant.Removed,
      },
      durationTile,
    ];
  }

  return [
    {
      labelKey: "etlJobLog.summaryLabel.rowsRead",
      value: `${rowsRead ?? 0}`,
      variant: EtlSummaryTileVariant.Existing,
    },
    {
      labelKey: "etlJobLog.summaryLabel.rowsWritten",
      value: `${rowsWritten ?? 0}`,
      variant: EtlSummaryTileVariant.New,
    },
    durationTile,
  ];
};
